import { DeleteOutline } from '@mui/icons-material'
import { Box, Menu, MenuItem, Typography, styled, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { IDeviation } from 'src/generated-types/deviation'
import { IFormSchemaReportDeviationDto } from 'src/generated-types/form-schema'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getThemeColor } from 'src/utils'

export interface DeviationMenuState {
  element: HTMLElement
  itemId: string
  fieldId: string
}

export interface IChecklistDeviationMenuProps {
  anchor: DeviationMenuState | null
  onClose: () => void
  deviations?: IDeviation[]
  onViewDeviation?: (deviationId: EntityId) => void
  pendingDeviations?: IFormSchemaReportDeviationDto[]
  onRemovePendingDeviation?: (tempId: string) => void
}

export const ChecklistDeviationMenu: React.FC<IChecklistDeviationMenuProps> = ({
  anchor,
  onClose,
  deviations,
  onViewDeviation,
  pendingDeviations,
  onRemovePendingDeviation,
}) => {
  const { t } = useTranslation(['deviation', 'common'])
  const theme = useTheme()

  return (
    <Menu
      anchorEl={anchor?.element}
      open={Boolean(anchor)}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <Box px={2} py={1}>
        <Typography variant="caption" fontWeight={600} color={getThemeColor(theme, MUIThemeColors.black)}>
          {t('linked-deviations', { ns: 'deviation' })}
        </Typography>
      </Box>
      {deviations?.map(deviation => (
        <MenuItem
          key={deviation.id}
          onClick={() => onViewDeviation?.(deviation.id)}
          sx={{ '&:hover': { bgcolor: getThemeColor(theme, MUIThemeColors.secondaryLight) } }}
        >
          <Flex.Column>
            <Typography variant="body2" fontWeight={500}>
              {deviation.title}
            </Typography>
          </Flex.Column>
        </MenuItem>
      ))}
      {pendingDeviations?.map(deviationDto => (
        <PendingDeviationMenuItem key={deviationDto.deviation.tempId} disableRipple>
          <Flex.Row alignItems="center" gap={1} width="100%" justifyContent="space-between">
            <Typography variant="body2" fontWeight={500}>
              {deviationDto.deviation.title}
            </Typography>
            {onRemovePendingDeviation && (
              <GridActionButton
                size="small"
                width="30px"
                toolTipTitle={t('remove', { ns: 'common' }) as string}
                buttonIcon={<DeleteOutline fontSize="small" />}
                onClick={e => {
                  e.stopPropagation()
                  onClose()
                  onRemovePendingDeviation(deviationDto.deviation.tempId as string)
                }}
              />
            )}
          </Flex.Row>
        </PendingDeviationMenuItem>
      ))}
    </Menu>
  )
}

const PendingDeviationMenuItem = styled(MenuItem)(() => ({
  cursor: 'default',
  '&:hover': {
    backgroundColor: 'transparent',
  },
}))
